import type { KeyboardEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ArrowRight, CalendarClock, Plus, Zap } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Skeleton } from '@/components/atoms/skeleton';
import {
  usePaymentScheduleTemplate,
  useTemplatePreview,
} from '@/hooks/payment-schedule-templates/usePaymentScheduleTemplate';
import { useEstimateSettings } from '@/hooks/settings/useEstimateSettings';
import { cn } from '@/lib/utils';
import { formatStageAmount } from '@/components/organisms/payment-schedules/paymentScheduleAmount';
import type { EstimateStatus } from '@/types/estimate';
import { EDITABLE_STATUSES } from './support/estimate-action-visibility';
import { STATUS_FILTERS } from './support/estimate-ui-helpers';
import { paymentPlanRows } from '@/lib/quote/paymentPlanRows';

interface Props {
  depositAmount?: number;
  depositRequired?: boolean;
  estimateId: string;
  grandTotal?: number;
  estimateStatus: EstimateStatus;
  /** Show the agreed schedule during job conversion without offering edits. */
  readOnly?: boolean;
  layout?: 'card' | 'form';
}

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

export function PaymentScheduleSection({
  depositAmount = 0,
  depositRequired = false,
  estimateId,
  grandTotal,
  estimateStatus,
  readOnly = false,
  layout = 'card',
}: Props) {
  const formLayout = layout === 'form';
  const navigate = useNavigate();
  const { data: tpl, isLoading } = usePaymentScheduleTemplate(estimateId);
  // The office's own card, not the customer's document.
  const { data: preview } = useTemplatePreview(estimateId, { audience: 'staff' });
  const { data: estimateSettings } = useEstimateSettings();

  const tenantDefaults =
    (estimateSettings as unknown as { paymentSchedule?: Record<string, unknown> })
      ?.paymentSchedule ?? {};
  const allowPaymentSchedules =
    tenantDefaults.allowPaymentSchedules === undefined
      ? true
      : Boolean(tenantDefaults.allowPaymentSchedules);
  const requireScheduleAboveAmount =
    typeof tenantDefaults.requireScheduleAboveAmount === 'number'
      ? tenantDefaults.requireScheduleAboveAmount
      : 0;
  const scheduleRequired =
    allowPaymentSchedules &&
    requireScheduleAboveAmount > 0 &&
    typeof grandTotal === 'number' &&
    grandTotal > requireScheduleAboveAmount;
  const hasDepositWorkflow = depositRequired || depositAmount > 0;
  const hasTemplate = Boolean(tpl);
  const isEditable = !readOnly && EDITABLE_STATUSES.includes(estimateStatus);
  const isApplicable = hasTemplate || isEditable || scheduleRequired || hasDepositWorkflow;

  if ((!allowPaymentSchedules && !tpl) || !isApplicable) return null;
  if (!hasTemplate && !isEditable && !hasDepositWorkflow && !scheduleRequired) return null;

  const previewPayload =
    preview != null && (preview as { stages?: unknown[] }).stages?.length
      ? (preview as PreviewPayload)
      : null;

  function goToEdit() {
    void navigate({
      to: '/dashboard/estimates/$id/payment-schedule',
      params: { id: estimateId },
      search: {} as never,
    });
  }

  return (
    <section
      className={
        formLayout
          ? 'grid scroll-mt-24 gap-y-3 border-t border-border/60 py-8 @min-[700px]:grid-cols-[200px_minmax(0,1fr)] @min-[700px]:gap-x-8 @min-[700px]:gap-y-4'
          : 'rounded-lg border bg-card px-4 py-3'
      }
    >
      <div
        className={cn('flex justify-between gap-3', formLayout ? 'items-start' : 'items-center')}
      >
        <div className="flex items-center gap-2">
          {!formLayout && <CalendarClock className="size-4 text-muted-foreground" />}
          <h2
            className={formLayout ? 'text-[13px] font-semibold leading-6' : 'text-sm font-semibold'}
          >
            Payment Schedule
          </h2>
        </div>
        {tpl && isEditable ? (
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={goToEdit}>
            Manage
          </Button>
        ) : null}
      </div>

      <div className={formLayout ? 'min-w-0' : 'mt-3'}>
        {isLoading ? (
          <SkeletonRows />
        ) : previewPayload ? (
          <StageList
            preview={previewPayload}
            onManage={isEditable ? goToEdit : undefined}
            plain={formLayout}
          />
        ) : hasTemplate ? (
          <SavedWithoutPreviewNote estimateStatus={estimateStatus} isEditable={isEditable} />
        ) : !isEditable ? (
          <ReadOnlyEmptyNote estimateStatus={estimateStatus} />
        ) : (
          <EmptyState scheduleRequired={scheduleRequired} onAdd={goToEdit} />
        )}
      </div>
    </section>
  );
}

function formatStatusLabel(status: EstimateStatus): string {
  return STATUS_FILTERS.find((option) => option.value === status)?.label ?? status;
}

function ReadOnlyEmptyNote({ estimateStatus }: { estimateStatus: EstimateStatus }) {
  return (
    <p className="rounded-md border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
      Payment schedule was not configured. Estimate is {formatStatusLabel(estimateStatus)};
      schedules can only be added in Draft, Review, or Revised.
    </p>
  );
}

function SavedWithoutPreviewNote({
  estimateStatus,
  isEditable,
}: {
  estimateStatus: EstimateStatus;
  isEditable: boolean;
}) {
  return (
    <p className="rounded-md border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
      Payment schedule is configured, but no preview is available yet.
      {isEditable
        ? null
        : ` Estimate is ${formatStatusLabel(
            estimateStatus,
          )}; schedules can only be edited in Draft, Review, or Revised.`}
    </p>
  );
}

interface PreviewPayload {
  /** The deposit in money — the stages are resolved net of it. */
  depositAmount?: number | null;
  stages: Array<{
    position: number;
    label: string;
    triggerDescription: string;
    amount: { kind: string; amount: number | null };
    autoCharge: boolean;
    action: string;
  }>;
  grandTotal: number;
  consentRequired: boolean;
  previewText: string | null;
}

function EmptyState({ scheduleRequired, onAdd }: { scheduleRequired: boolean; onAdd: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-8 text-center text-muted-foreground">
      <CalendarClock className="h-8 w-8 text-muted-foreground/40" />
      <div className="space-y-0.5">
        <p className="text-sm font-medium text-foreground">No payment schedule</p>
        <p className="text-xs">
          {scheduleRequired
            ? 'A payment schedule is required before approval. Define when charges fire as the estimate progresses.'
            : 'Define when invoices and charges fire as the estimate progresses.'}
        </p>
      </div>
      <Button variant="outline" size="sm" onClick={onAdd}>
        <Plus className="mr-1.5 h-3.5 w-3.5" />
        Add payment schedule
      </Button>
    </div>
  );
}

function SkeletonRows() {
  return (
    <div className="space-y-2">
      <Skeleton className="h-16 w-full rounded-lg" />
      <Skeleton className="h-16 w-full rounded-lg" />
    </div>
  );
}

function StageList({
  preview,
  onManage,
  plain = false,
}: {
  preview: PreviewPayload;
  onManage?: () => void;
  plain?: boolean;
}) {
  const depositRow = paymentPlanRows(preview).find((row) => row.isDeposit);

  return (
    <div className="space-y-3">
      {preview.previewText ? (
        <p
          className={
            plain ? 'text-sm text-muted-foreground' : 'rounded-md bg-muted/40 px-3 py-2 text-sm'
          }
        >
          {preview.previewText}
        </p>
      ) : null}
      <div className={plain ? 'divide-y divide-border/60' : 'space-y-2'}>
        {/* The deposit is billed at acceptance out of this same total and the
            stages are resolved net of it, so the office sees the row too —
            otherwise its plan disagrees with the customer's. */}
        {depositRow && depositRow.amount != null ? (
          <div
            className={
              plain
                ? 'flex items-center justify-between py-2 text-sm'
                : 'flex items-center justify-between rounded-md border px-3 py-2 text-sm'
            }
          >
            <span>
              <span className="font-medium">{depositRow.label}</span>
              <span className="text-muted-foreground"> · {depositRow.detail}</span>
            </span>
            <span className="tabular-nums">{currency.format(depositRow.amount)}</span>
          </div>
        ) : null}
        {preview.stages.map((stage) => (
          <StageRow
            key={`${stage.position}-${stage.label}`}
            stage={stage}
            onClick={onManage}
            plain={plain}
          />
        ))}
      </div>
      <div className="flex items-center justify-between border-t pt-2 text-sm font-semibold">
        <span>Total</span>
        <span className="tabular-nums">{currency.format(preview.grandTotal)}</span>
      </div>
      {preview.consentRequired ? (
        <p className="rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning">
          Customer consent is required at acceptance because this schedule includes auto-charge.
        </p>
      ) : null}
    </div>
  );
}

function StageRow({
  stage,
  onClick,
  plain = false,
}: {
  stage: PreviewPayload['stages'][number];
  onClick?: () => void;
  plain?: boolean;
}) {
  const clickable = Boolean(onClick);
  const interactiveProps = clickable
    ? {
        role: 'button' as const,
        tabIndex: 0,
        onClick,
        onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClick?.();
          }
        },
      }
    : {};

  return (
    <div
      {...interactiveProps}
      className={cn(
        'group flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between',
        plain
          ? 'py-3 first:pt-0 last:pb-0'
          : 'rounded-lg border bg-background px-4 py-3 transition-colors',
        clickable && 'cursor-pointer hover:border-muted-foreground/40',
      )}
    >
      <div className="min-w-0">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <p
            className={cn(
              'text-foreground',
              plain ? 'text-sm font-medium' : 'text-base font-semibold',
            )}
          >
            {stage.label}
          </p>
          {stage.autoCharge ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning">
              <Zap className="size-3" />
              Auto-charge
            </span>
          ) : null}
        </div>
        <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
          {!plain && <ArrowRight className="size-3.5 shrink-0" />}
          <span className={plain ? undefined : 'truncate'}>{stage.triggerDescription}</span>
        </p>
      </div>

      <div className="flex shrink-0 items-center gap-3 text-sm font-semibold tabular-nums text-foreground sm:justify-end">
        {formatStageAmount(stage.amount)}
      </div>
    </div>
  );
}
